'use client';

import themeVariables from '@/config/theme/themeVariable';
import CenterLayout from '@/layout/CenterLayout';
import { useRef, useState, useEffect } from 'react';
import NaverMap, { NaverMapHandle } from '@/components/NaverMap';
import { useLanguage } from '@/hooks/useLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import Skeleton from '../_components/MapViewSkeleton';
import { useFetchLocalPlaces } from './_hook';
import { LocalPlace } from '@/types/places';
import { Maximize2, Minimize2, UserIcon } from 'lucide-react';


export default function LocalPlacesClientPage() {
  const mapRef = useRef<NaverMapHandle>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { data: localPlaces, loading } = useFetchLocalPlaces();
  const { language } = useLanguage();
  const { t } = useTranslation();


  // 전체화면 모드일 때 페이지 스크롤 제어
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isFullscreen]);

  // 카드 클릭 핸들러
  const handleCardClick = (markerIndex: number) => {
    if (mapRef.current) {
      mapRef.current.focusOnMarker(markerIndex);
      const mapElement = document.querySelector('[data-map-container]');
      if (mapElement) {
        mapElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // 전체화면 토글 핸들러
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // 혼잡도에 따른 아이콘 개수 반환
  const getCrowdLevelIcons = (level: number) => {
    const iconCount = level;
    return Array.from({ length: 5 }, (_, index) => (
      <UserIcon
        key={index} 
        className={`w-3.5 h-3.5 stroke-[2.7px] ${
          index < iconCount 
            ? (level <= 2 ? 'text-green-500' :
               level <= 3 ? 'text-yellow-500' :
               level <= 4 ? 'text-orange-500' : 'text-red-500')
            : 'text-gray-300'
        }`} 
      />
    ));
  };

  // localPlaces 데이터를 마커 형태로 변환
  const markers = localPlaces?.result?.map((place: LocalPlace, index: number) => ({
    lat: place.location.lat,
    lng: place.location.lng,
    title: place.name[language],
    description: (
      <div>
        <p className="mb-3 text-sm leading-relaxed text-gray-600">{place.description[language]}</p>
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600 font-medium">{t('peak_hours')}:</span>
            <span className="text-gray-600">{place.peak_hours[language]}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600 font-medium">{t('crowd_level')}:</span>
            <div className="flex items-center gap-0.5">
              {getCrowdLevelIcons(place.crowd_level)}
            </div>
          </div>
        </div>
      </div>
    ),
    icon: (
      <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg border-2 border-white ${
        place.crowd_level <= 2 ? 'bg-green-500' :
        place.crowd_level <= 3 ? 'bg-yellow-500' :
        place.crowd_level <= 4 ? 'bg-orange-500' : 'bg-red-500'
      }`}>
        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
        </svg>
      </div>
    )
  })) || [];

  // 로딩 상태 처리
  if (!localPlaces?.result) {
    return (
      <CenterLayout>
        <Skeleton />
      </CenterLayout>
    );
  }

  return (
    <CenterLayout>
    <div className={`transition-all duration-500 ease-in-out ${isFullscreen ? 'pt-20' : ''}`}>
      <div className={`mb-8 transition-opacity duration-300 ${isFullscreen ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100'}`}>
        <div className="flex justify-between items-start mb-4">
          <div className='mb-6'>
            <h1 className="text-3xl font-bold text-gray-900 pt-4 mb-4">{t('local_places_title')}</h1>
            <p className="text-gray-600">
              {t('local_places_subtitle')}
            </p>
          </div>
        </div>
      </div>
      
      <div className="p-1">
        <div className={`flex flex-col lg:flex-row gap-6 transition-all duration-500 ease-in-out ${
          isFullscreen 
            ? 'fixed inset-0 z-50 bg-white pt-20 px-4' 
            : 'relative'
        }`}>
          {/* 지도 영역 - 왼쪽 */}
          <div className={`${isFullscreen ? 'w-3/4' : 'lg:w-3/4'} transition-all duration-500 ease-in-out`} data-map-container>
            <div className={isFullscreen ? '' : 'sticky top-4'}>
              <div className={`transition-all duration-500 ease-in-out ${
                isFullscreen ? 'h-[calc(100vh-6rem)]' : 'h-[400px] lg:h-[720px]'
              } rounded-lg overflow-hidden shadow-lg`}>
                <NaverMap ref={mapRef} markers={markers} zoom={14}/>
              </div>
            </div>
          </div>
          
          {/* 카드 목록 영역 - 오른쪽 */}
          <div className={`${isFullscreen ? 'w-1/4' : 'lg:w-1/4'} transition-all duration-500 ease-in-out ${
            isFullscreen ? 'h-[calc(100vh-6rem)]' : 'h-[400px] lg:h-[720px]'
          } flex flex-col`}>
            <div className="flex-shrink-0 mb-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-gray-800">{t('places_list')}</h3>
                <div
                  className={`${themeVariables.badge.gray} cursor-pointer hover:bg-gray-100 transition-all duration-300 hidden xl:block`}
                  onClick={toggleFullscreen}
                >
                  <div className="transition-transform duration-200">
                    {isFullscreen ? (
                      <div className="flex items-center gap-1.5">
                        <Minimize2 className="w-3 h-3 transition-transform duration-300" />
                        {t('back')}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <Maximize2 className="w-3 h-3 transition-transform duration-300" />
                        {t('fullscreen')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600">{t('click_to_view_location')}</p>
            </div>
            
            {/* 장소 정보 카드들 */}
            <div className="flex-1 space-y-4 overflow-y-auto pr-2 transition-all duration-300">
              {localPlaces.result.map((place: LocalPlace, index: number) => (
                <div 
                  key={place.id} 
                  className="p-3 rounded-lg cursor-pointer bg-white border border-gray-200 active:border-gray-300"
                  onClick={() => handleCardClick(index)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-3 h-3 rounded-full flex-shrink-0 mt-1 ${
                      place.crowd_level <= 2 ? 'bg-green-500' :
                      place.crowd_level <= 3 ? 'bg-yellow-500' :
                      place.crowd_level <= 4 ? 'bg-orange-500' : 'bg-red-500'
                    }`}></div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 text-base mb-2 truncate">{place.name[language]}</h3>
                      <p className="text-sm text-gray-700 mb-3 leading-relaxed break-words">{place.description[language]}</p>
                      <div className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">{t('peak_hours')}</span>: {place.peak_hours[language]}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="font-medium">{t('crowd_level')}</span>: 
                        <div className="inline-flex items-center gap-0.5 ml-2">
                          {getCrowdLevelIcons(place.crowd_level)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
    </CenterLayout>
  );
}


